import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE from '../config';

function UploadBox({ preview, onChange, onRemove, dragging, onDragEnter, onDragLeave, onDrop, label, accent }) {
  const ref = useRef();
  return (
    <div className="form-group">
      <label>{label}</label>
      <div
        className={`upload-area ${dragging ? 'dragging' : ''}`}
        style={{
          borderColor: dragging ? accent : undefined,
          background: dragging ? `${accent}08` : undefined,
        }}
        onClick={() => ref.current.click()}
        onDrop={onDrop}
        onDragOver={e => { e.preventDefault(); onDragEnter(); }}
        onDragLeave={onDragLeave}
      >
        <input
          type="file"
          accept="image/*"
          ref={ref}
          style={{ display: 'none' }}
          onChange={e => onChange(e.target.files[0])}
        />
        {preview ? (
          <img src={preview} alt="preview" className="upload-preview" />
        ) : (
          <>
            <span className="upload-icon">🖼</span>
            <span className="upload-text">클릭하거나 드래그해서 올리세요</span>
          </>
        )}
      </div>
      {preview && (
        <button className="remove-btn" onClick={onRemove}>✕ 이미지 제거</button>
      )}
    </div>
  );
}

export default function ImageToImage({ onSave }) {
  const { token } = useAuth();
  const [mode, setMode] = useState('edit');
  const [prompt, setPrompt] = useState('');
  const [file1, setFile1] = useState(null);
  const [preview1, setPreview1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [preview2, setPreview2] = useState(null);
  const [drag1, setDrag1] = useState(false);
  const [drag2, setDrag2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const ACCENT = 'var(--accent4)';

  const loadFile = (file, setFile, setPreview) => {
    if (!file || !file.type.startsWith('image/')) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!file1 || !prompt.trim()) return;
    if (mode === 'merge' && !file2) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const toBase64 = f => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = e => res(e.target.result);
        reader.onerror = rej;
        reader.readAsDataURL(f);
      });
      const body = { prompt, image: await toBase64(file1) };
      if (mode === 'merge' && file2) body.image2 = await toBase64(file2);

      const res = await fetch(`${API_BASE}/api/i2i`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data && data.success) {
        // {images: [...]} 또는 {image: '...'} 두 형식 모두 처리
        let url;
        if (Array.isArray(data.images) && data.images.length > 0) {
          url = data.images[0];
        } else if (data.image) {
          url = data.image;
        }
        setResult(url || null);
        if (url) onSave({ type: 'i2i', prompt, url });
      } else {
        setError((data && data.error) || '알 수 없는 오류');
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = result;
    a.download = `grok-i2i-${Date.now()}.png`;
    a.click();
  };

  return (
    <>
      <div className="panel">
        <div className="panel-header">
          <div className="panel-icon cyan">✏️</div>
          <div className="panel-title-group">
            <div className="panel-title">이미지 → 이미지</div>
            <div className="panel-sub">이미지 편집 또는 두 이미지 합성</div>
          </div>
        </div>
        <div className="panel-body">
          <div className="form-col">
            <div className="form-group">
              <label>모드</label>
              <div className="mode-grid">
                {[['edit', '✏️ 이미지 편집'], ['merge', '🔀 이미지 합성']].map(([m, lbl]) => (
                  <button
                    key={m}
                    className="mode-btn"
                    onClick={() => setMode(m)}
                    style={{
                      border: mode === m ? `2px solid var(--accent4)` : '1px solid var(--border)',
                      background: mode === m ? 'rgba(0,188,212,0.12)' : 'var(--bg3)',
                      color: mode === m ? 'var(--accent4)' : 'var(--text2)',
                      fontWeight: mode === m ? '700' : '400',
                    }}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            <UploadBox
              preview={preview1}
              onChange={f => loadFile(f, setFile1, setPreview1)}
              onRemove={() => { setFile1(null); setPreview1(null); }}
              dragging={drag1}
              onDragEnter={() => setDrag1(true)}
              onDragLeave={() => setDrag1(false)}
              onDrop={e => { e.preventDefault(); setDrag1(false); loadFile(e.dataTransfer.files[0], setFile1, setPreview1); }}
              label={mode === 'merge' ? '이미지 1 (얼굴/캐릭터)' : '원본 이미지'}
              accent="var(--accent4)"
            />

            {mode === 'merge' && (
              <UploadBox
                preview={preview2}
                onChange={f => loadFile(f, setFile2, setPreview2)}
                onRemove={() => { setFile2(null); setPreview2(null); }}
                dragging={drag2}
                onDragEnter={() => setDrag2(true)}
                onDragLeave={() => setDrag2(false)}
                onDrop={e => { e.preventDefault(); setDrag2(false); loadFile(e.dataTransfer.files[0], setFile2, setPreview2); }}
                label="이미지 2 (포즈/배경)"
                accent="var(--accent4)"
              />
            )}

            <div className="form-group">
              <label>프롬프트 (필수)</label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder={mode === 'edit' ? '수채화 스타일로 변환해줘...' : '1번 이미지의 얼굴을 2번 이미지에 합성해줘...'}
                rows={4}
              />
            </div>

            <button
              className="btn btn-cyan"
              onClick={generate}
              disabled={loading || !file1 || !prompt.trim() || (mode === 'merge' && !file2)}
            >
              {loading ? '⏳ 생성 중...' : '✏️ 이미지 생성'}
            </button>
            {error && <div className="error-box">⚠ {error}</div>}
          </div>

          <div className="output-area">
            <span className="output-label">결과</span>
            <div className="output-box">
              {loading ? (
                <div className="loading">
                  <div className="loading-bar">
                    <div className="loading-bar-fill" style={{ background: 'var(--accent4)' }} />
                  </div>
                  <span className="loading-text">이미지 생성 중...</span>
                </div>
              ) : result ? (
                <img src={result} alt="result" className="output-image" onClick={() => setLightbox(result)} />
              ) : (
                <div className="output-placeholder">
                  <span className="output-placeholder-icon">✏️</span>
                  <span className="output-placeholder-text">이미지가 여기 표시됩니다</span>
                </div>
              )}
            </div>
            {result && <button className="btn-download" onClick={download}>↓ PNG 다운로드</button>}
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="lightbox" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}
